import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, Flex, FormControl, FormLabel, Grid, GridItem, Input, InputGroup, InputLeftElement, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, Center, Badge } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import MyDiv from './bulkUpdate.style';
import CustomToast from '../../components/CustomToast';
import { useFetch } from '../../hooks/useFetch';
import CustomTables, { Column, ILazyParams } from '../../components/CustomTable/CustomTables';


interface IEmployee {
  id: number;
  employeeCode: string;
  name: string;
  department: string | null;
  businessProcess: string | null;
  shift: string | null;
}

const BulkUpdate = () => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [companies, setCompanies] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [businessProcesses, setBusinessProcesses] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [branchId, setBranchId] = useState<number | null>(null);
  const [departmentId, setDepartmentId] = useState<number | null>(null);

  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);


  const [isOpen, setIsOpen] = useState(false);
  const [bulkBP, setBulkBP] = useState<number | null>(null);
  const [bulkShift, setBulkShift] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [lazyParams, setLazyParams] = useState<ILazyParams>({ first: 0, rows: 20, sortField: undefined, sortOrder: undefined, multiSortMeta: [], filters: {}, globalFilter: '', });

  useEffect(() => {
    (async () => {
      const [c, d, bp, s] = await Promise.all([
        fetchApi('DropDown/company', 'GET'),
        fetchApi('DropDown/department', 'GET'),
        fetchApi('Hrlense_BusinessProcess', 'GET'),
        fetchApi('Hrlense_Shift', 'GET'),
      ]);
      if (c) setCompanies(c);
      if (d) setDepartments(d);
      if (bp) setBusinessProcesses(bp);
      if (s) setShifts(s);
    })();
  }, []);



  useEffect(() => {
    if (!companyId || !branchId || !departmentId) {
      setEmployees([]);
      setTotalCount(0);
      setSearched(false);
      setSelectedIds([]);
      return;
    }

    loadEmployees();
  }, [companyId, branchId, departmentId, lazyParams]);

  const loadEmployees = async () => {
    setLoading(true);

    try {
      setSelectedIds([]);

      const params = new URLSearchParams();

      if (companyId) { params.set("companyId", String(companyId)); }
      if (branchId) { params.set("branchId", String(branchId)); }
      if (departmentId) { params.set("departmentId", String(departmentId)); }
      params.set("first", String(lazyParams.first));
      params.set("rows", String(lazyParams.rows));
      if (lazyParams.sortField) { params.set("sortField", lazyParams.sortField); }
      if (lazyParams.sortOrder !== undefined) { params.set("sortOrder", String(lazyParams.sortOrder)); }
      if (lazyParams.globalFilter) { params.set("globalFilter", lazyParams.globalFilter); }
      if (lazyParams.filters && Object.keys(lazyParams.filters).length) { params.set("filters", JSON.stringify(lazyParams.filters)); }

      const res = await fetchApi<any>(`User/bulkEmployees?${params.toString()}`, "GET");

      if (res) {
        setEmployees(res.data ?? []);
        setTotalCount(res.totalRecords ?? 0);
      } else {
        setEmployees([]);
        setTotalCount(0);
      }

      setSearched(true);
    } finally {
      setLoading(false);
    }
  };



  const allSelected = employees.length > 0 && employees.every((e) => selectedIds.includes(e.id));
  const someSelected = employees.some((e) => selectedIds.includes(e.id));

  const toggleOne = (id: number) => setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => {
    const ids = employees.map((e) => e.id);
    setSelectedIds((p) => (allSelected ? p.filter((id) => !ids.includes(id)) : Array.from(new Set([...p, ...ids]))));
  };

  const closeModal = () => {
    setIsOpen(false);
    setBulkBP(null);
    setBulkShift(null);
  };

  const handleUpdate = async () => {
    if (!bulkBP) return addToast({ message: 'Please select Business Process.', status: 'error' });
    if (!bulkShift) return addToast({ message: 'Please select Shift.', status: 'error' });

    setSaving(true);
    const res = await fetchApi('User/bulkUpdate', 'PUT', {
      loginIds: selectedIds,
      businessProcessId: bulkBP,
      shiftId: bulkShift,
    });
    setSaving(false);

    if (res) {
      addToast({ message: `${selectedIds.length} employee(s) updated successfully.`, status: 'success' });
      closeModal();
      loadEmployees();
    }
  };

  const clearFilters = () => {
    setCompanyId(null);
    setBranchId(null);
    setDepartmentId(null);
  };

  useEffect(() => {
    if (!companyId) { setBranches([]); return; }
    (async () => {
      const b = await fetchApi(`Dropdown/branchWithCompanyBulkup?companyId=${companyId}`, 'GET');
      setBranches(b ?? []);
    })();
  }, [companyId]);

  return (
    <MyDiv>
      <Flex className="page_heading flex_header" justifyContent="space-between" alignItems="center">
        <Text className="font-poppins text_semibold text_3xl">Bulk Update Employee</Text>
        {selectedIds.length > 0 && (
          <Button colorScheme="green" size="sm" onClick={() => setIsOpen(true)}>
            Bulk Update ({selectedIds.length})
          </Button>
        )}
      </Flex>

      <Box className="overview_card" bg="white" border="1px solid #E2E8F0" p={4} mt={3}>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4} alignItems="end">
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Company</FormLabel>
              <Select placeholder="Select Company" value={companyId ?? ''} onChange={(e) => {
                setCompanyId(e.target.value ? Number(e.target.value) : null);
                setBranchId(null);
                setDepartmentId(null);
                setLazyParams(prev => ({ ...prev, first: 0, }));
              }}
              >
                {companies.map((c) => (<option key={c.company_ID} value={c.company_ID}>{c.company_Name}</option>))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Branch</FormLabel>
              <Select placeholder="Select Branch" isDisabled={!companyId} value={branchId ?? ''} onChange={(e) => {
                setBranchId(e.target.value ? Number(e.target.value) : null);
                setDepartmentId(null);
                setLazyParams(prev => ({ ...prev, first: 0, }));
              }}
              >
                {branches.map((b) => (<option key={b.branchId} value={b.branchId}>{b.branchName}</option>))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Department</FormLabel>
              <Select
                placeholder="Select Department"
                isDisabled={!branchId}
                value={departmentId ?? ''}
                onChange={(e) => {
                  setDepartmentId(e.target.value ? Number(e.target.value) : null);
                  setLazyParams(prev => ({ ...prev, first: 0 }));
                }}
              >
                {departments.map((d) => (<option key={d.id} value={d.id}>{d.departmentName}</option>))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <Button variant="outline" onClick={clearFilters} w="full">Clear</Button>
          </GridItem>
        </Grid>
      </Box>

      <Text className="font-poppins text_semibold text_md" mt={4}>
        Employees {searched && <Badge ml={2} colorScheme="blue">{employees.length}</Badge>}
      </Text>

      <Box mt={4} position="relative" minH="300px">
        {!searched && !loading ? (
          <Box bg="white" border="1px solid #E2E8F0" borderRadius="10px">
            <Text textAlign="center" color="gray.500" py={12}>
              Please select a Company, Branch, & Department to view the employee list.
            </Text>
          </Box>
        ) : (
          <CustomTables<IEmployee>
            value={employees}
            loading={loading}
            emptyMessage="No employees found."
            preferenceKey="bulkUpdateTable"
            lazy
            lazyParams={lazyParams}
            onLazyParamsChange={setLazyParams}
            totalRecords={totalCount}
            showGlobalSearch={true}
            showToolbar={true}
            showPagination
            rowsPerPageOptions={[20, 100, 200, 500]}
            globalSearchPlaceholder="Search by name or code..."
            maxTableHeight={null}

          >
            <Column header={<Checkbox isChecked={allSelected} isIndeterminate={someSelected && !allSelected} onChange={toggleAll} />}
              frozen="left"
              body={(e: IEmployee) => (
                <Checkbox isChecked={selectedIds.includes(e.id)} onChange={() => toggleOne(e.id)} />
              )}
            />
            <Column header="Emp. Code" field="employeeCode" sortable filterable filterType="text" frozen="left" />
            <Column header="Employee Name" field="name" sortable filterable filterType="text" frozen="left" />
            <Column header="Department" field="department" sortable filterable filterType="text" />
            <Column header="Business Process" field="businessProcess" sortable filterable filterType="text" />
            <Column header="Shift" field="shift" />
          </CustomTables>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={closeModal} isCentered size="md">
        <ModalOverlay />
        <ModalContent className="font-poppins text_md text_regular">
          <ModalHeader borderBottom="1px solid #E2E8F0">Bulk Update Employees</ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            <Text mb={4}>Selected Employees: <b>{selectedIds.length}</b></Text>

            <FormControl mb={4}>
              <FormLabel>Business Process</FormLabel>
              <Select placeholder="Select Business Process" value={bulkBP ?? ''} onChange={(e) => setBulkBP(e.target.value ? Number(e.target.value) : null)}>
                {businessProcesses.filter((b) => b.status).map((b) => (<option key={b.id} value={b.id}>{b.businessProcessName}</option>))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Shift</FormLabel>
              <Select placeholder="Select Shift" value={bulkShift ?? ''} onChange={(e) => setBulkShift(e.target.value ? Number(e.target.value) : null)}>
                {shifts.filter((s) => s.status !== false).map((s) => (<option key={s.id} value={s.id}>{s.shift}</option>))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter borderTop="1px solid #E2E8F0" gap={2}>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button colorScheme="green" isLoading={saving} onClick={handleUpdate}>Update</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MyDiv>
  );
};

export default BulkUpdate;